import React, { useEffect, useState } from "react";
import { adminApi } from "../lib/api";
import { inr, timeFrom } from "../lib/format";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";

const STATUS_CLS = {
  won: "border-primary/40 text-primary",
  lost: "border-red-500/40 text-red-400",
  void: "border-zinc-500/40 text-zinc-400",
  cancelled: "border-zinc-500/40 text-zinc-400",
};

const matches = (b, q) =>
  !q || [b.username, b.userName, b.ref, b.matchLabel, b.sessionName, b.teamName].some((f) => String(f || "").toLowerCase().includes(q));

export default function AdminOtherSlips({ token, kind, search, refreshKey, onCount }) {
  const { toast } = useToast();
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    adminApi.betsByKind(token, kind)
      .then((r) => { setBets(r); onCount?.(r.length); })
      .catch(() => toast({ title: "Could not load bet slips" }))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [token, kind, refreshKey]);

  const cancel = async (b) => {
    if (!window.confirm(`Cancel ${b.ref}? Full stake ${inr(b.stake)} is refunded to ${b.username || "user"} and the bet is voided.`)) return;
    try {
      await adminApi.cancelSessionBet(token, b.id);
      toast({ title: "Bet cancelled \u2705", description: `${b.ref} \u2014 ${inr(b.stake)} refunded.` });
      load();
    } catch (e) {
      toast({ title: "Could not cancel", description: e?.response?.data?.detail || "Try again." });
    }
  };

  const q = search.trim().toLowerCase();
  const rows = bets.filter((b) => matches(b, q));
  const isToss = kind === "toss";

  return (
    <div className="rounded-2xl border border-border bg-card divide-y divide-border" data-testid={`admin-${kind}-slips-list`}>
      {loading && bets.length === 0 && <div className="px-5 py-8 text-center text-muted-foreground">Loading…</div>}
      {!loading && rows.length === 0 && <div className="px-5 py-8 text-center text-muted-foreground">No {kind} bets yet.</div>}
      {rows.map((b) => (
        <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4" data-testid={`admin-${kind}-slip-${b.ref}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary" data-testid={`admin-${kind}-slip-user-${b.ref}`}>{b.username || b.userName || "—"}</span>
              <span className="text-xs text-muted-foreground">· {b.ref}</span>
              {isToss ? (
                <span className="text-[10px] font-bold uppercase rounded px-1.5 py-0.5 bg-sky-500/20 text-sky-300">{b.teamName}</span>
              ) : (
                <span className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${b.side === "yes" ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>
                  {b.side} {b.line}
                </span>
              )}
              <Badge variant="outline" className={STATUS_CLS[b.status] || "border-amber-500/40 text-amber-400"}>{b.status}</Badge>
            </div>
            <div className="text-xs text-muted-foreground mt-0.5 truncate">
              {b.matchLabel} · {isToss ? "Toss winner" : <>{b.sessionName}{b.inning ? ` · Inn ${b.inning}` : ""}</>}
              {b.result != null && <> · result <span className="text-foreground">{b.result}</span></>} · {timeFrom(b.placedAt)}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="font-semibold">Stake {inr(b.stake)}</div>
              <div className="text-xs text-muted-foreground">Returns {inr(b.potentialReturn)}</div>
            </div>
            {!isToss && b.status === "open" && (
              <Button size="sm" variant="outline" data-testid={`admin-cancel-${kind}-${b.ref}`} onClick={() => cancel(b)}
                className="bg-red-500/10 border-red-500/40 text-red-400 hover:bg-red-500/20">
                Cancel
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
